import React, { useState, useRef, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { navigateToTenant } from '../utils/tenantUtils';
import { tenantService, type TenantRegistrationData } from '../services/tenantService';
import { clearUserDataForLogout } from '../utils/localStorageCleanup';
import { authService } from '../services/authService';
import ReCAPTCHA from 'react-google-recaptcha';

interface RegistrationData {
  orgName: string;
  subdomain: string;
  admin: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
  };
}

interface SubdomainStatus {
  checking: boolean;
  available: boolean | null;
  message: string;
}

const Registration: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [formData, setFormData] = useState<RegistrationData>({
    orgName: '',
    subdomain: '',
    admin: {
      name: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  const [subdomainStatus, setSubdomainStatus] = useState<SubdomainStatus>({
    checking: false,
    available: null,
    message: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // CAPTCHA state
  const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);
  const [captchaError, setCaptchaError] = useState('');

  // Debounce timer ref for subdomain checking
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleCaptchaChange = React.useCallback(async (token: string | null) => {
    if (token) {
      try {
        await authService.captcha(token);
        setIsCaptchaVerified(true);
        setCaptchaError('');
      } catch (error) {
        console.error('error:', error);
        startTransition(() => {
          setCaptchaError('Invalid captcha');
          setIsCaptchaVerified(false);
        });
      }
    }
  }, []);

  // Auto-verify CAPTCHA in development mode
  React.useEffect(() => {
    if (import.meta.env.MODE === 'development') {
      setIsCaptchaVerified(true);
    }
  }, []);

  const validateSubdomain = (subdomain: string): boolean => {
    const subdomainRegex = /^[a-z0-9-]+$/;
    return subdomainRegex.test(subdomain) && subdomain.length >= 3 && subdomain.length <= 63;
  };

  const checkSubdomainAvailability = async (subdomain: string) => {
    if (!validateSubdomain(subdomain)) {
      setSubdomainStatus({
        checking: false,
        available: false,
        message:
          'Subdomain must be 3-63 characters long and contain only lowercase letters, numbers, and hyphens'
      });
      return;
    }

    setSubdomainStatus({ checking: true, available: null, message: 'Checking availability...' });

    try {
      const result = await tenantService.checkSubdomainAvailability(subdomain);

      setSubdomainStatus({
        checking: false,
        available: result.available,
        message: result.available ? 'Subdomain is available!' : 'Subdomain is already taken'
      });
    } catch {
      setSubdomainStatus({
        checking: false,
        available: false,
        message: 'Error checking subdomain availability'
      });
    }
  };

  const handleSubdomainChange = (value: string) => {
    const cleanValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData((prev) => ({
      ...prev,
      subdomain: cleanValue
    }));

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Clear previous status only if we're starting a new check
    if (cleanValue.length >= 3) {
      // Only show checking status if we're going to actually check
      setSubdomainStatus({ checking: false, available: null, message: '' });

      // Debounced check with proper cleanup
      debounceTimerRef.current = setTimeout(() => {
        checkSubdomainAvailability(cleanValue);
      }, 600); // Balanced delay for responsiveness and API efficiency
    } else {
      // Clear status immediately for short values
      setSubdomainStatus({ checking: false, available: null, message: '' });
    }
  };

  const handleChange = (field: string, value: string) => {
    if (field.startsWith('admin.')) {
      const adminField = field.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        admin: {
          ...prev.admin,
          [adminField]: value
        }
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.orgName.trim()) {
      newErrors.orgName = 'Organization name is required';
    }

    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Subdomain is required';
    } else if (!validateSubdomain(formData.subdomain)) {
      newErrors.subdomain = 'Invalid subdomain format';
    } else if (subdomainStatus.available === false) {
      newErrors.subdomain = 'Subdomain is not available';
    }

    if (!formData.admin.name.trim()) {
      newErrors['admin.name'] = 'Admin name is required';
    }

    if (!formData.admin.email.trim()) {
      newErrors['admin.email'] = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin.email)) {
      newErrors['admin.email'] = 'Invalid email format';
    }

    if (!formData.admin.password) {
      newErrors['admin.password'] = 'Password is required';
    } else if (formData.admin.password.length < 6) {
      newErrors['admin.password'] = 'Password must be at least 6 characters';
    }

    if (formData.admin.password !== formData.admin.confirmPassword) {
      newErrors['admin.confirmPassword'] = 'Passwords do not match';
    }

    if (!isCaptchaVerified) {
      setCaptchaError('Please verify captcha');
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (subdomainStatus.available !== true) {
      setErrors({ subdomain: 'Please ensure subdomain is available' });
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const registrationData: TenantRegistrationData = {
        orgName: formData.orgName,
        subdomain: formData.subdomain,
        admin: {
          name: formData.admin.name,
          email: formData.admin.email,
          password: formData.admin.password
        }
      };

      const result = await tenantService.registerTenant(registrationData);

      if (result.success) {
        // Registration successful - redirect to email verification page
        // Clear any previous user data to ensure clean slate for new tenant
        clearUserDataForLogout();

        if (window.location.hostname === 'localhost') {
          // For local development, store tenant ID and redirect to verify email page
          localStorage.setItem('tenantId', formData.subdomain);
          navigate(`/verify-email?email=${encodeURIComponent(formData.admin.email)}`);
        } else {
          // For production, navigate to the tenant subdomain's verify email page
          navigateToTenant(formData.subdomain, `/verify-email?email=${encodeURIComponent(formData.admin.email)}`);
        }
      } else {
        setSubmitError(result.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSubdomainStatusIcon = () => {
    if (subdomainStatus.checking) {
      return (
        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
      );
    }
    if (subdomainStatus.available === true) {
      return (
        <svg
          className="h-4 w-4 text-green-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    if (subdomainStatus.available === false) {
      return (
        <svg className="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      );
    }
    return null;
  };

  const cannotSubmit =
    isSubmitting || subdomainStatus.available !== true || !isCaptchaVerified || !!captchaError;

  return (
    <div
      className={`registration-page min-h-screen ${
        theme === 'dark'
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
      }`}
    >
      {/* Scrollable container */}
      <div className="min-h-screen overflow-y-auto overflow-x-hidden">
        <div className="flex items-center justify-center py-8 px-4 sm:py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-lg space-y-8">
            {/* Main card */}
            <div
              className={`relative p-6 sm:p-8 rounded-xl shadow-xl backdrop-blur-sm border transition-all duration-300 ${
                theme === 'dark'
                  ? 'bg-gray-800/90 shadow-gray-900/50 border-gray-700/50'
                  : 'bg-white/95 shadow-blue-100/50 border-gray-200/50'
              }`}
            >
              {/* Header */}
              <div className="text-center mb-8">
                <div className="mx-auto h-14 w-14 mb-6">
                  <img
                    src="/favicon.png"
                    alt={t('common.appLogo')}
                    className="h-full w-full object-contain drop-shadow-sm"
                  />
                </div>
                <h1
                  className={`text-2xl sm:text-3xl font-bold tracking-tight ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  Register Your Organization
                </h1>
                <p
                  className={`mt-3 text-sm sm:text-base ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Create your organization's ticketing system
                </p>
              </div>

              {/* Error message */}
              {submitError && (
                <div
                  className={`rounded-lg p-4 mb-6 animate-[slideIn_0.3s_ease-out] border ${
                    theme === 'dark'
                      ? 'bg-red-900/20 border-red-800/30 backdrop-blur-sm'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className={`h-5 w-5 mt-0.5 ${
                          theme === 'dark' ? 'text-red-400' : 'text-red-500'
                        }`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <p
                        className={`text-sm font-medium ${
                          theme === 'dark' ? 'text-red-200' : 'text-red-800'
                        }`}
                      >
                        {submitError}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Registration form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Organization Name */}
                <div>
                  <label
                    htmlFor="orgName"
                    className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Organization Name *
                  </label>
                  <input
                    id="orgName"
                    type="text"
                    value={formData.orgName}
                    onChange={(e) => handleChange('orgName', e.target.value)}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } ${errors.orgName ? 'border-red-500' : ''}`}
                    placeholder="Enter your organization name"
                  />
                  {errors.orgName && <p className="mt-1 text-sm text-red-500">{errors.orgName}</p>}
                </div>

                {/* Subdomain */}
                <div>
                  <label
                    htmlFor="subdomain"
                    className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Subdomain *
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="subdomain"
                      type="text"
                      value={formData.subdomain}
                      onChange={(e) => handleSubdomainChange(e.target.value)}
                      className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } ${errors.subdomain ? 'border-red-500' : ''}`}
                      placeholder="your-org"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {getSubdomainStatusIcon()}
                    </div>
                  </div>
                  {subdomainStatus.message && (
                    <p
                      className={`mt-1 text-sm ${
                        subdomainStatus.available === true
                          ? 'text-green-600'
                          : subdomainStatus.available === false
                            ? 'text-red-500'
                            : 'text-gray-500'
                      }`}
                    >
                      {subdomainStatus.message}
                    </p>
                  )}
                  {errors.subdomain && (
                    <p className="mt-1 text-sm text-red-500">{errors.subdomain}</p>
                  )}
                  <p
                    className={`mt-1 text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Your organization will be accessible at: {formData.subdomain}.flowcxp.com
                  </p>
                </div>

                {/* Admin Details */}
                <div
                  className={`border-t pt-6 ${
                    theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
                  }`}
                >
                  <h3
                    className={`text-lg font-medium mb-4 ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                    }`}
                  >
                    Administrator Account
                  </h3>

                  {/* Admin Name */}
                  <div className="mb-4">
                    <label
                      htmlFor="adminName"
                      className={`block text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Full Name *
                    </label>
                    <input
                      id="adminName"
                      type="text"
                      value={formData.admin.name}
                      onChange={(e) => handleChange('admin.name', e.target.value)}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } ${errors['admin.name'] ? 'border-red-500' : ''}`}
                      placeholder="Enter admin full name"
                    />
                    {errors['admin.name'] && (
                      <p className="mt-1 text-sm text-red-500">{errors['admin.name']}</p>
                    )}
                  </div>

                  {/* Admin Email */}
                  <div className="mb-4">
                    <label
                      htmlFor="adminEmail"
                      className={`block text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Email Address *
                    </label>
                    <input
                      id="adminEmail"
                      type="email"
                      value={formData.admin.email}
                      onChange={(e) => handleChange('admin.email', e.target.value)}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } ${errors['admin.email'] ? 'border-red-500' : ''}`}
                      placeholder="admin@yourorg.com"
                    />
                    {errors['admin.email'] && (
                      <p className="mt-1 text-sm text-red-500">{errors['admin.email']}</p>
                    )}
                  </div>

                  {/* Admin Password */}
                  <div className="mb-4">
                    <label
                      htmlFor="adminPassword"
                      className={`block text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Password *
                    </label>
                    <div className="mt-1 relative">
                      <input
                        id="adminPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.admin.password}
                        onChange={(e) => handleChange('admin.password', e.target.value)}
                        className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        } ${errors['admin.password'] ? 'border-red-500' : ''}`}
                        placeholder="Enter password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <svg
                          className={`h-5 w-5 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          {showPassword ? (
                            <>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 11-4.243-4.243m4.242 4.242L9.88 9.88"
                              />
                            </>
                          ) : (
                            <>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                    {errors['admin.password'] && (
                      <p className="mt-1 text-sm text-red-500">{errors['admin.password']}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className={`block text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Confirm Password *
                    </label>
                    <div className="mt-1 relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.admin.confirmPassword}
                        onChange={(e) => handleChange('admin.confirmPassword', e.target.value)}
                        className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        } ${errors['admin.confirmPassword'] ? 'border-red-500' : ''}`}
                        placeholder="Confirm password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        <svg
                          className={`h-5 w-5 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          {showConfirmPassword ? (
                            <>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 11-4.243-4.243m4.242 4.242L9.88 9.88"
                              />
                            </>
                          ) : (
                            <>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                    {errors['admin.confirmPassword'] && (
                      <p className="mt-1 text-sm text-red-500">{errors['admin.confirmPassword']}</p>
                    )}
                  </div>
                </div>

                {/* CAPTCHA Section */}
                {import.meta.env.MODE !== 'development' && (
                  <div
                    className={`border-t pt-6 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}
                  >
                    <ReCAPTCHA
                      sitekey={import.meta.env.VITE_CAPTCHA_SITE_KEY}
                      onChange={handleCaptchaChange}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={cannotSubmit}
                    className={`flex-1 flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      cannotSubmit
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 transform hover:scale-[1.02]'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Creating Organization...
                      </>
                    ) : (
                      <>
                        <svg
                          className="h-4 w-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Create Organization
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Footer */}
              <div
                className={`mt-8 pt-6 border-t text-center ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Already have an organization?{' '}
                  <a
                    href="/login"
                    className={`font-semibold transition-colors duration-200 ${
                      theme === 'dark'
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-blue-600 hover:text-blue-700'
                    }`}
                  >
                    Sign in here
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;
